import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ExternalLink, FileText } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { Section } from '@/shared/ui/layout/Section';
import { DataTable } from '@/shared/ui/data-table/DataTable';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { Button } from '@/shared/ui/buttons/Button';
import { extractErrorMessage } from '@/shared/api/errors';
import { useClientReceipts } from '@/entities/client';
import { formatDateTime } from '@/shared/utils/date';
import { formatUsd } from '@/shared/utils/currency';
import type { ApiClientReceiptItem } from '@/shared/types/api/clients';

type Props = {
  clientId: string;
};

const openInNewTab = (url: string): void => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

export const PaymentHistorySection = ({ clientId }: Props) => {
  const { t } = useTranslation();
  const query = useClientReceipts(clientId);

  const columns = useMemo<ColumnDef<ApiClientReceiptItem>[]>(
    () => [
      {
        accessorKey: 'number',
        header: t('clients.receipts.columns.number'),
        enableSorting: false,
        cell: (info) => (
          <span className="font-mono text-xs font-medium text-ink">{info.row.original.number}</span>
        ),
      },
      {
        id: 'parcel',
        header: t('clients.receipts.columns.parcel'),
        enableSorting: false,
        cell: (info) => {
          const row = info.row.original;
          const name = row.parcel_name?.trim();
          const trek = row.trek_id?.trim();
          if (!name && !trek) {
            return <span className="text-ink-faint">—</span>;
          }
          const content = (
            <span className="flex flex-col">
              {name ? <span className="text-sm text-ink">{name}</span> : null}
              {trek ? <span className="font-mono text-xs text-ink-muted">{trek}</span> : null}
            </span>
          );
          if (row.parcel_id) {
            return (
              <Link to={`/parcels/${row.parcel_id}`} className="hover:underline">
                {content}
              </Link>
            );
          }
          return content;
        },
      },
      {
        accessorKey: 'amount',
        header: t('clients.receipts.columns.amount'),
        enableSorting: false,
        cell: (info) => (
          <span className="text-sm font-medium text-ink">
            {formatUsd(Number(info.row.original.amount))}
          </span>
        ),
      },
      {
        accessorKey: 'created_at',
        header: t('clients.receipts.columns.createdAt'),
        enableSorting: false,
        cell: (info) => (
          <span className="text-xs text-ink-soft">{formatDateTime(info.row.original.created_at)}</span>
        ),
      },
      {
        id: 'actions',
        header: t('clients.receipts.columns.actions'),
        enableSorting: false,
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                leftIcon={<ExternalLink className="h-3.5 w-3.5" aria-hidden />}
                disabled={!row.public_url}
                onClick={() => row.public_url && openInNewTab(row.public_url)}
              >
                {t('clients.receipts.view')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                leftIcon={<FileText className="h-3.5 w-3.5" aria-hidden />}
                disabled={!row.file_url}
                onClick={() => row.file_url && openInNewTab(row.file_url)}
              >
                {t('clients.receipts.pdf')}
              </Button>
            </div>
          );
        },
      },
    ],
    [t],
  );

  return (
    <Section title={t('clients.receipts.title')}>
      {query.error ? (
        <ErrorState
          message={extractErrorMessage(query.error, t('clients.receipts.loadError'))}
          onRetry={() => void query.refetch()}
        />
      ) : (
        <DataTable
          data={query.data ?? []}
          columns={columns}
          isLoading={query.isLoading}
          emptyTitle={t('clients.receipts.empty')}
          getRowId={(row, index) => row.id || String(index)}
        />
      )}
    </Section>
  );
};
